import { DeleteOutlined, EditOutlined } from "@ant-design/icons"
import { Typography } from "antd"
import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import type { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../../../../hooks/use-date-formatter.tsx"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { AttachmentModels } from "../../../../../../Album/types/attachment.types.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import type { Payment } from "../../../../../types/project-finance.types.tsx"

interface Props {
  payments: Payment[]
  currentUserId: string
  projectId: string
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  onEdit?: (p: Payment) => void
  onDelete?: (p: Payment) => void
}

const DETAILS_MIN_WIDTH = 130
// Fixed rather than measured: the list shares the drawer's scrolling column with
// the summary above it, and a fitted height would take the whole scrollport and
// push everything after it off-screen. Matches `CustomerRelatedCard`, the other
// table in that position. It also gives the row menu a body to be clipped in —
// AntD renders none without a `y`.
const HISTORY_BODY_HEIGHT = 320

export const PaymentsHistoryList: React.FC<Props> = ({
  payments,
  currentUserId,
  projectId,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation()
  const { timezone, dateFormat } = useDateFormatter()
  const columns: ColumnsType<Payment> = [
    {
      title: t("finance.paymentsDrawer.columns.date"),
      dataIndex: "payment_date",
      key: "payment_date",
      width: 110,
      render: (value: string | undefined) =>
        value ? dayjs.utc(value).tz(timezone).format(dateFormat) : "—"
    },
    {
      title: t("finance.paymentsDrawer.columns.amount"),
      dataIndex: "value",
      key: "value",
      width: 100,
      render: (raw: string) => {
        const amount = parseFloat(raw)
        const isRefund = amount < 0
        return (
          <Typography.Text type={isRefund ? "warning" : undefined}>
            {isRefund ? "−" : ""}
            {budgetFormat(Math.abs(amount))}
          </Typography.Text>
        )
      }
    },
    {
      title: t("finance.paymentsDrawer.columns.method"),
      dataIndex: "payment_method_display",
      key: "method",
      width: 120,
      ellipsis: true
    },
    {
      title: t("finance.paymentsDrawer.columns.status"),
      dataIndex: "status_display",
      key: "status_display",
      width: 100,
      ellipsis: true
    },
    {
      title: t("finance.paymentsDrawer.columns.details"),
      dataIndex: "payment_details",
      key: "details",
      ellipsis: true
    },
    {
      title: t("finance.paymentsDrawer.columns.invoice"),
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 120,
      ellipsis: true,
      render: (value: string | null | undefined) => value || "—"
    },
    {
      title: t("finance.paymentsDrawer.columns.by"),
      dataIndex: "created_by_name",
      key: "by",
      width: 120,
      ellipsis: true,
      render: (value: string | null | undefined) => value ?? null
    },
    {
      title: t("finance.paymentsDrawer.columns.attachments"),
      key: "attachments",
      width: 110,
      align: "center",
      render: (_: unknown, record: Payment) => (
        <AttachmentsController
          key={record.id}
          currentUserId={currentUserId}
          project_id={projectId}
          object_id={record.id}
          model_type={AttachmentModels.PARTIAL_TRANSACTION_ITEM}
          attachments={record.attachments}
          addAllowed={uploadFileAllowed}
          deleteAllowed={deleteFileAllowed}
          deleteForeignAllowed={deleteForeignFileAllowed}
        />
      )
    }
  ]

  const actionsColumn: ColumnsType<Payment>[number] = {
    title: "",
    key: "actions",
    width: 52,
    align: "center",
    fixed: "right",
    render: (_: unknown, record: Payment) => (
      <RowActionsMenu
        placement="bottomRight"
        buttonProps={{
          size: "small",
          "aria-label": t("finance.paymentsDrawer.actions.menuLabel")
        }}
        items={[
          ...(onEdit
            ? [
                {
                  key: "edit",
                  label: t("finance.paymentsDrawer.actions.edit"),
                  icon: <EditOutlined />,
                  onClick: () => onEdit(record)
                }
              ]
            : []),
          ...(onDelete
            ? [
                {
                  key: "delete",
                  label: t("finance.paymentsDrawer.actions.delete"),
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onDelete(record)
                }
              ]
            : [])
        ]}
      />
    )
  }

  const allColumns = onEdit || onDelete ? [...columns, actionsColumn] : columns

  const tableMinWidth =
    allColumns.reduce(
      (sum, column) =>
        sum + (typeof column.width === "number" ? column.width : 0),
      0
    ) + DETAILS_MIN_WIDTH

  return (
    <EntityTable<Payment>
      surface={TABLE_SURFACES.paymentsHistory}
      selectionMode="on-demand"
      tab={DEFAULT_TAB}
          hideConfigButton
      size="small"
      rowKey="id"
      columns={allColumns}
      dataSource={payments}
      withOutPagination
          pagination={false}
      scroll={{ x: tableMinWidth, y: HISTORY_BODY_HEIGHT }}
    />
  )
}
